import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../../shared/api/client";
import type { Permission, Role } from "../../shared/api/types";
import { useI18n } from "../../shared/i18n";
import { Icon } from "../../shared/ui/icons";
import { AdminTabs } from "./AdminTabs";
import { SkeletonRows } from "../../shared/ui/Skeleton";

export function RolesPage() {
  const { t } = useI18n();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  // First load only, so a refetch keeps the current rows rather than replacing a
  // populated table with placeholders on every change.
  const [firstLoad, setFirstLoad] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ roles: Role[]; permissions: Permission[] }>("/roles");
      setRoles(r.roles);
      setPermissions(r.permissions);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("roles.loadFailed"));
    } finally {
      // In a finally: a failed first load must show the error, not shimmer forever.
      setFirstLoad(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Grouped by module so the grid reads as "what may this role do in Accounting"
  // rather than as one flat list of twelve keys.
  const byModule = useMemo(() => {
    const groups = new Map<string, Permission[]>();
    for (const p of permissions) {
      const list = groups.get(p.module) ?? [];
      list.push(p);
      groups.set(p.module, list);
    }
    return [...groups.entries()];
  }, [permissions]);

  async function toggle(role: Role, key: string) {
    const next = role.permissions.includes(key)
      ? role.permissions.filter((k) => k !== key)
      : [...role.permissions, key];
    try {
      await api.put(`/roles/${role.id}/permissions`, { permissions: next });
      setSaved(role.name);
      setError(null);
      await load();
      window.setTimeout(() => setSaved(null), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("roles.saveFailed"));
    }
  }

  return (
    <div className="stack">
      <AdminTabs />
      {error && (
        <p className="banner error" role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p className="banner ok" role="status">
          <Icon name="check" size={15} />
          {t("roles.saved", { name: saved })}
        </p>
      )}

      <h2 className="section-title">{t("roles.title")}</h2>
      <p className="muted small">{t("roles.help")}</p>

      {firstLoad ? (
        <SkeletonRows rows={6} columns={[2, 1, 1, 1, 1, 1, 1]} label={t("roles.title")} />
      ) : (
      <div className="table-scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th className="sticky-col">{t("roles.permission")}</th>
              {roles.map((r) => (
                <th key={r.key} className="cell-center" title={r.description}>
                  {r.name}
                  <span className="th-sub">{t("roles.userCount", { n: r.user_count })}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byModule.map(([module, perms]) => (
              <Fragment key={module}>
                <tr className="group-row">
                  <th className="sticky-col" colSpan={roles.length + 1}>
                    {module}
                  </th>
                </tr>
                {perms.map((p) => (
                  <tr key={p.key}>
                    <th className="sticky-col" title={p.description}>
                      <code>{p.action}</code>
                    </th>
                    {roles.map((r) => {
                      const on = r.permissions.includes(p.key);
                      const locked = r.key === "admin";
                      return (
                        <td key={r.key + p.key} className="cell-center">
                          <button
                            type="button"
                            className={`tick ${on ? "on" : ""} ${locked ? "locked" : ""}`}
                            disabled={locked}
                            aria-pressed={on}
                            aria-label={`${r.name}: ${p.key}`}
                            title={locked ? t("roles.adminLocked") : p.key}
                            onClick={() => void toggle(r, p.key)}
                          >
                            <Icon name="check" size={15} />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
